import {
  CreditCard, Loader2, MapPin, Star, Check,
  Bookmark, BookmarkCheck,
} from "lucide-react";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";

export const CountryCard = ({
  rec,
  index,
  isBookmarked,
  onToggleBookmark,
  isInBundle,
  onToggleBundle,
  onPurchase,
  purchasing,
}) => {
  const testId = rec.country_name.toLowerCase().replace(/\s+/g, "-");
  return (
    <div
      className={`bg-card rounded-2xl border border-primary/10 overflow-hidden card-hover ${
        index === 0 ? "md:col-span-2 lg:col-span-1 lg:row-span-2" : ""
      }`}
      data-testid={`country-card-${testId}`}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img src={rec.image_url} alt={rec.country_name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

        {/* Match Score */}
        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1">
          <span className="font-mono text-lg font-bold text-primary">
            {rec.match_score}%
          </span>
        </div>

        {/* Bookmark */}
        <button
          onClick={() => onToggleBookmark(rec)}
          className="absolute top-4 left-4 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors"
          aria-label={isBookmarked ? "Remove bookmark" : "Save country"}
          data-testid={`bookmark-toggle-${testId}`}
        >
          {isBookmarked ? (
            <BookmarkCheck className="h-5 w-5 text-secondary" />
          ) : (
            <Bookmark className="h-5 w-5 text-muted-foreground" />
          )}
        </button>

        {/* Country Name */}
        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex items-center gap-2 text-white">
            <MapPin className="h-5 w-5" />
            <h3 className="text-2xl font-bold">{rec.country_name}</h3>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-6">
        {/* Bundle Selection */}
        <div className="flex items-center gap-3 mb-4 p-3 bg-accent/5 rounded-lg border border-accent/20">
          <Checkbox
            checked={isInBundle}
            onCheckedChange={() => onToggleBundle(rec.country_name)}
            data-testid={`bundle-select-${testId}`}
          />
          <span className="text-sm font-medium">
            {isInBundle ? (
              <span className="text-accent flex items-center gap-1">
                <Check className="h-4 w-4" /> Added to bundle
              </span>
            ) : (
              "Add to bundle for savings"
            )}
          </span>
        </div>

        <p className="text-foreground/80 mb-4">{rec.summary}</p>

        {/* Recommended Regions */}
        {rec.recommended_regions?.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-primary mb-2 flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              Best Regions For You
            </h4>
            <div className="flex flex-wrap gap-2">
              {/* eslint-disable-next-line react/no-array-index-key --
                  Static LLM response, never reorders; Emergent's visual-edits
                  babel plugin chokes on expression keys in named-export components. */}
              {rec.recommended_regions.slice(0, 4).map((region, i) => (
                <div key={i} className="group relative">
                  <span className="inline-block px-2 py-1 bg-accent/10 text-accent text-xs rounded-full cursor-help border border-accent/20">
                    {region.name}
                  </span>
                  {region.why && (
                    <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-10 w-48 p-2 bg-card border border-primary/10 rounded-lg shadow-lg text-xs text-muted-foreground">
                      {region.why}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Highlights */}
        <div className="space-y-2 mb-6">
          {/* eslint-disable-next-line react/no-array-index-key -- see note above */}
          {(rec.highlights || []).slice(0, 3).map((highlight, i) => (
            <div key={i} className="flex items-start gap-2">
              <Star className="h-4 w-4 text-secondary flex-shrink-0 mt-0.5" />
              <span className="text-sm text-muted-foreground">{highlight}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <Button
          onClick={() => onPurchase(rec.country_name)}
          disabled={purchasing}
          className="w-full btn-animated bg-secondary hover:bg-secondary/90 gap-2"
          data-testid={`buy-report-${testId}`}
        >
          {purchasing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <CreditCard className="h-4 w-4" />
              Get Full Guide - $20
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default CountryCard;
